import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip, Typography, type ColumnsType, type ColumnType } from '@app/ui';
import type { PostData } from '@app/components/social-posts/types';
import * as format from '@app/utils/formatters';
import * as sorter from '@app/utils/sorters';
import isImage from '@app/utils/isImage';
import shortenStr from '@app/utils/shortenStr';
import { getCustomIcon } from '@app/utils/getSocialIcons';
import { Post } from '@app/social-media-posts/types';
import DisplayHashtags from '@app/social-media-posts/DisplayHashtags';
import DisplayPlcSp from '@app/social-media-posts/DisplayPlcSp';
import DisplayCollaboratingAccounts from '@app/social-media-posts/DisplayCollaboratingAccounts';

const { Text } = Typography;

type AuthorshipType = 'standard' | 'collaboration' | 'unsupported';

const AUTHORSHIP_TYPE_LABEL: Record<AuthorshipType, string> = {
    standard: 'filters.authorshipType.standard',
    collaboration: 'filters.authorshipType.collaboration',
    unsupported: 'filters.authorshipType.unsupported',
};

const listMediaStyle = {
    width: '6rem',
    display: 'block',
    margin: '0px auto',
    borderRadius: '8px',
};

interface ColumnOptions {
    isSelectMode: boolean;
    postLevelMetrics: boolean;
    organicAndPaidMetrics: boolean;
    showCollabPostInfo?: boolean;
    onDetailedView?: (post: Post) => void;
    onSelectPost?: (
        postId: number,
        selected: { [key: string]: boolean },
        setSelected: (selected: { [key: string]: boolean }) => void,
    ) => void;
}

export const useColumns = ({
    organicAndPaidMetrics,
    showCollabPostInfo = false,
    onDetailedView,
}: ColumnOptions): ColumnsType<PostData> => {
    const { t } = useTranslation(['measurement', 'common']);

    return useMemo(() => {
        const baseColumns: ColumnsType<PostData> = [
            {
                title: t('socialMediaExposure.table.media'),
                dataIndex: 'cdnMediaUrl',
                key: 'media',
                align: 'center',
                width: 120,
                render: (url: string, post) => {
                    const isVideo = post.mediaType.id === 2 && !isImage(url);
                    return (
                        <div
                            className="-p-h-1 -cursor--pointer"
                            onClick={() => onDetailedView?.(post)}
                        >
                            {isVideo ? (
                                <video style={listMediaStyle} src={url} />
                            ) : (
                                <img style={listMediaStyle} src={url} />
                            )}
                        </div>
                    );
                },
            },
            {
                title: t('socialMediaExposure.table.date'),
                dataIndex: 'createdAt',
                key: 'date',
                align: 'left',
                width: 100,
                render: (date: string) => <span>{format.shortDate(date)}</span>,
                sorter: (a, b) => sorter.date(a.createdAt, b.createdAt),
            },
            {
                title: t('socialMediaExposure.table.platform'),
                dataIndex: 'socialPlatform',
                key: 'platform',
                width: 100,
                align: 'center',
                render: (platform: string) => {
                    const lowerSocName = platform.toLowerCase();
                    return (
                        <Tooltip title={platform}>
                            {getCustomIcon(lowerSocName, true)}
                        </Tooltip>
                    );
                },
                sorter: (a, b) =>
                    sorter.string(a.socialPlatform, b.socialPlatform),
            },
            {
                title: t('socialMediaExposure.table.account'),
                dataIndex: 'socialAccount',
                key: 'account',
                align: 'left',
                width: 140,
                render: (account: any) => {
                    const { username } = account;
                    if (!username) return null;
                    return (
                        <Tooltip title={username}>
                            <Text
                                style={{
                                    fontFamily: 'pnregular',
                                    fontSize: '14px',
                                }}
                            >
                                {shortenStr(15, username)}
                            </Text>
                        </Tooltip>
                    );
                },
                sorter: (a, b) =>
                    sorter.string(
                        a.socialAccount.username,
                        b.socialAccount.username,
                    ),
            },
            {
                title: t('socialMediaExposure.table.hashtags'),
                dataIndex: 'hashtags',
                key: 'hashtags',
                width: 150,
                align: 'left',
                render: (hashtags: string[]) => {
                    if (!hashtags || hashtags.length === 0) return null;
                    return <DisplayHashtags hashtags={hashtags} />;
                },
            },
            {
                title: t('socialMediaExposure.table.placementSponsor'),
                dataIndex: 'sponsorAnnotationsDetails',
                key: 'placementSponsor',
                align: 'left',
                width: 150,
                render: (assoc: any[]) => {
                    if (!assoc || assoc.length === 0) return null;
                    return <DisplayPlcSp assoc={assoc} />;
                },
            },
        ];

        if (showCollabPostInfo) {
            baseColumns.push(
                {
                    title: t('socialMediaExposure.table.authorshipType'),
                    dataIndex: 'authorshipType',
                    key: 'authorshipType',
                    align: 'left',
                    width: 130,
                    render: (value: string | null | undefined) => {
                        if (value == null || value === '') return null;
                        const labelKey = value
                            ? AUTHORSHIP_TYPE_LABEL[value as AuthorshipType] ??
                              null
                            : null;
                        const label = labelKey
                            ? t(labelKey, { ns: 'common' })
                            : value;
                        return (
                            <Text
                                style={{
                                    fontFamily: 'pnregular',
                                    fontSize: '14px',
                                }}
                            >
                                {label}
                            </Text>
                        );
                    },
                    sorter: (a, b) =>
                        sorter.string(
                            a.authorshipType ?? '',
                            b.authorshipType ?? '',
                        ),
                },
                {
                    title: t('socialMediaExposure.table.collaboratingAccounts'),
                    dataIndex: 'collaboratingAccounts',
                    key: 'collaboratingAccounts',
                    align: 'left',
                    width: 150,
                    render: (
                        accounts: string | string[] | null | undefined,
                    ) => <DisplayCollaboratingAccounts accounts={accounts} />,
                },
            );
        }

        const renderMetric = (
            title: string,
            dataIndex: keyof PostData,
        ): ColumnType<PostData> => ({
            title,
            dataIndex,
            width: 120,
            className: organicAndPaidMetrics && 'group-header-extra',
            key: dataIndex,
            align: 'left',
            sorter: (a, b) =>
                ((a[dataIndex] as number) ?? 0) -
                ((b[dataIndex] as number) ?? 0),
            render: (val: number) =>
                val != null ? (
                    <Text style={{ fontFamily: 'pnregular', fontSize: '14px' }}>
                        {format.decimal(val)}
                    </Text>
                ) : (
                    '-'
                ),
        });

        const metricColumns: ColumnsType<PostData> = organicAndPaidMetrics
            ? [
                  {
                      title: t('socialMediaExposure.table.organic'),
                      className: 'group-header-extra',
                      children: [
                          renderMetric(
                              t('socialMediaExposure.table.impressions'),
                              'organicImpressions',
                          ),
                          renderMetric(
                              t('socialMediaExposure.table.videoViews'),
                              'organicVideoViews',
                          ),
                          renderMetric(
                              t('socialMediaExposure.table.engagements'),
                              'organicEngagements',
                          ),
                      ],
                  },
                  {
                      title: t('socialMediaExposure.table.paid'),
                      className: 'group-header-extra',
                      key: 'paid-values',
                      children: [
                          renderMetric(
                              t('socialMediaExposure.table.impressions'),
                              'paidImpressions',
                          ),
                          renderMetric(
                              t('socialMediaExposure.table.videoViews'),
                              'paidVideoViews',
                          ),
                          renderMetric(
                              t('socialMediaExposure.table.engagements'),
                              'paidEngagements',
                          ),
                      ],
                  },
              ]
            : [
                  renderMetric(
                      t('socialMediaExposure.table.impressions'),
                      'impressions',
                  ),
                  renderMetric(
                      t('socialMediaExposure.table.videoViews'),
                      'videoViews',
                  ),
                  renderMetric(
                      t('socialMediaExposure.table.engagements'),
                      'engagements',
                  ),
              ];

        return organicAndPaidMetrics
            ? [
                  {
                      title: '',
                      children: baseColumns,
                  },
                  ...metricColumns,
              ]
            : [...baseColumns, ...metricColumns];
    }, [t, organicAndPaidMetrics, showCollabPostInfo, onDetailedView]);
};
